import React from 'react';
import '../../../css/DictionaryStyles.scss';
import { register } from '../../../i18n';

const t = register('GlobalQuestionLabels');
const t2 = register('AltDictionary');

const OxfordDictionaryDisplay = ({ dictionaryData }) => {
  // Early return if no data is provided
  if (!dictionaryData || !dictionaryData.results || dictionaryData.results.length === 0) {
    // Remove the noResults message from here. Only render content if there is data.
    return null;
  }

  const { results } = dictionaryData;
  const entry = results[0];

  return (
    <div className='oxford-dictionary-display'>
      <div className='max-w-4xl mx-auto p-4 bg-white rounded-lg font-sans'>
        <header className='border-b border-gray-200 pb-0 mb-6'>
          <div className='flex items-center justify-between'>
            <h1 className='text-3xl font-bold text-gray-800 mt-0'>{entry.word}</h1>
            {/* <span className='text-sm text-gray-500'>
              {entry.language === 'en-gb' ? 'British English' : entry.language}
            </span> */}
          </div>
        </header>

        {entry.lexicalEntries?.map((lexEntry, lexIndex) => (
          <section key={`lex-${lexIndex}`} className='mb-8'>
            {lexEntry.lexicalCategory && (
              <h2 className='text-xl font-semibold text-gray-700 mb-3'>
                {lexEntry.lexicalCategory.text || ''}
              </h2>
            )}

            {lexEntry.entries?.map((entryItem, entryIndex) => (
              <div key={`entry-${entryIndex}`} className='mb-6'>
                {/* Pronunciations section */}
                {entryItem.pronunciations && entryItem.pronunciations.length > 0 && (
                  <div className='mb-4 flex flex-wrap items-center'>
                    {entryItem.pronunciations.map((pron, pronIndex) => (
                      <span key={`pron-${pronIndex}`} className='mr-4 text-gray-600 flex items-center'>
                        {pron.phoneticSpelling && (
                          <span className='inline-block'>/{pron.phoneticSpelling}/</span>
                        )}
                        {pron.dialects && pron.dialects.length > 0 && (
                          <span className='text-xs text-gray-500 ml-1'>
                            ({pron.dialects.join(', ')})
                          </span>
                        )}
                        {pron.audioFile && (
                          <button
                            className='ml-2 p-1 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700'
                            onClick={() => {
                              const audio = new Audio(pron.audioFile);
                              audio.play();
                            }}
                            title={t('listenToPronunciation')}
                            aria-label={t('listenToPronunciation')}
                          >
                            <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'
                              xmlns='http://www.w3.org/2000/svg'>
                              <path d='M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z' strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} />
                            </svg>
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                {/* Etymology section */}
                {entryItem.etymologies && entryItem.etymologies.length > 0 && (
                  <div className='mb-4 italic text-gray-600 text-sm'>
                    <span className='font-medium text-gray-700'>{t2('origin')}: </span>
                    {entryItem.etymologies.join(' ')}
                  </div>
                )}

                {/* Senses (definitions) section */}
                <div className='space-y-4'>
                  {entryItem.senses && entryItem.senses.map((sense, senseIndex) => (
                    <div key={`sense-${senseIndex}`} className='ml-0'>
                      <div className='flex'>
                        <span className='mr-2 text-gray-500 font-medium'>{senseIndex + 1}.</span>
                        <div className='flex-1'>
                          {/* Definition */}
                          <div className='font-medium text-gray-800'>
                            {sense.definitions?.[0]}
                          </div>

                          {/* Domain classes */}
                          {sense.domainClasses && sense.domainClasses.length > 0 && (
                            <div className='text-sm text-gray-500 italic mt-1'>
                              ({sense.domainClasses.map((domain) => domain.text).join(', ')})
                            </div>
                          )}

                          {/* Register information */}
                          {sense.registers && sense.registers.length > 0 && (
                            <div className='text-sm text-blue-600 mt-1'>
                              {sense.registers.map((reg) => reg.text).join(', ')}
                            </div>
                          )}

                          {/* Examples */}
                          {sense.examples && sense.examples.length > 0 && (
                            <ul className='mt-2 space-y-1'>
                              {sense.examples.map((example, exIndex) => (
                                <li key={`example-${exIndex}`} className='text-gray-600 italic text-sm pl-4 border-l-2 border-gray-200'>
                                  &ldquo;{example.text}&rdquo;
                                  {example.registers && example.registers.length > 0 && (
                                    <span className='text-xs text-blue-500 ml-2 not-italic'>
                                      ({example.registers.map((reg) => reg.text).join(', ')})
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}

                          {/* Synonyms */}
                          {sense.synonyms && sense.synonyms.length > 0 && (
                            <div className='mt-2'>
                              <span className='text-sm font-medium text-gray-700'>{t2('synonyms')}: </span>
                              <span className='text-sm text-gray-600'>
                                {sense.synonyms.map((syn) => syn.text).join(', ')}
                              </span>
                            </div>
                          )}

                          {/* Subsenses (nested definitions) */}
                          {sense.subsenses && sense.subsenses.length > 0 && (
                            <div className='mt-3 ml-4 space-y-3'>
                              {sense.subsenses.map((subsense, subIndex) => (
                                <div key={`subsense-${subIndex}`}>
                                  <div className='flex'>
                                    <span className='mr-2 text-gray-500'>{senseIndex + 1}.{subIndex + 1}</span>
                                    <div className='flex-1'>
                                      <div className='font-medium text-gray-700'>
                                        {subsense.definitions?.[0]}
                                      </div>

                                      {/* Domain classes for subsense */}
                                      {subsense.domainClasses && subsense.domainClasses.length > 0 && (
                                        <div className='text-sm text-gray-500 italic mt-1'>
                                          ({subsense.domainClasses.map((domain) => domain.text).join(', ')})
                                        </div>
                                      )}

                                      {/* Examples for subsense */}
                                      {subsense.examples && subsense.examples.length > 0 && (
                                        <ul className='mt-2 space-y-1'>
                                          {subsense.examples.map((example, exIndex) => (
                                            <li key={`subex-${exIndex}`} className='text-gray-600 italic text-sm pl-4 border-l-2 border-gray-200'>
                                              &ldquo;{example.text}&rdquo;
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
};

export default OxfordDictionaryDisplay;
